<template>
  <div class="h-full flex flex-col gap-3 overflow-hidden">
    <!-- Experiment description -->
    <div class="px-5 py-4 rounded border border-gray-200 bg-white">
      <p class="text-sm text-gray-600 leading-relaxed">
        <strong class="text-gray-800">Production-realistic readmission prediction.</strong>
        Each patient was scored at the moment of hospital discharge using only data available in real-time:
        an ADT (Admit/Discharge/Transfer) notification from the hospital, plus the patient's full claims history
        with a 14-day processing lag. No DRG, no billing detail from the index stay — just what a care manager
        would actually have on discharge day. The model (GPT-5.4) reads the patient's compacted clinical graph
        and returns a 30-day readmission probability in a single API call with no training or fine-tuning.
        597 discharges from March–May 2025, 70 readmissions (11.7%).
      </p>
    </div>

    <!-- Two-panel content -->
    <div class="flex-1 flex overflow-hidden rounded border border-gray-200 bg-white">
    <!-- Left panel: table -->
    <div class="flex-1 flex flex-col overflow-hidden" :class="selectedPrediction ? 'w-1/2' : 'w-full'">
      <!-- Header bar -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div class="flex items-center gap-4">
          <h3 class="text-sm font-semibold text-gray-900">
            30-Day Readmission Risk
            <span v-if="predictions.length" class="text-gray-400 font-normal ml-1">{{ filteredPredictions.length }} patients</span>
          </h3>
          <div class="flex items-center gap-1.5">
            <button
              v-for="f in outcomeFilters"
              :key="f.value"
              class="px-2.5 py-1 text-xs rounded-full border transition-colors"
              :class="activeOutcome === f.value
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'"
              @click="activeOutcome = f.value"
            >{{ f.label }}</button>
          </div>
        </div>
        <div class="flex items-center gap-3 text-xs text-gray-500">
          <span>Model: <strong class="text-gray-700">GPT-5.4</strong></span>
          <span>AUROC: <strong class="text-gray-700">0.65</strong></span>
          <span>LACE AUROC: <strong class="text-gray-400">0.56</strong></span>
        </div>
      </div>

      <!-- Table -->
      <div class="flex-1 overflow-auto">
        <div v-if="loading" class="p-4">
          <div v-for="i in 12" :key="i" class="h-10 bg-gray-50 rounded mb-1 animate-pulse" />
        </div>

        <table v-else class="w-full text-xs">
          <thead class="sticky top-0 bg-gray-50 z-10">
            <tr>
              <th
                v-for="col in columns"
                :key="col.field"
                class="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap"
                :class="col.sortable !== false ? 'cursor-pointer select-none hover:text-gray-700' : ''"
                @click="col.sortable !== false && toggleSort(col.field)"
              >
                {{ col.label }}
                <span v-if="sortField === col.field" class="ml-0.5">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="p in paginatedPredictions"
              :key="p.MBI + '_' + p.INDEX_CLM_ID"
              class="border-t border-gray-100 cursor-pointer transition-colors"
              :class="{
                'bg-blue-50': isSelected(p),
                'hover:bg-gray-50': !isSelected(p)
              }"
              @click="selectPatient(p)"
            >
              <td class="px-3 py-2 font-mono text-gray-700">{{ p.MBI }}</td>
              <td class="px-3 py-2 text-gray-600">{{ p.DISCHARGE_DT }}</td>
              <td class="px-3 py-2">
                <span
                  class="inline-block px-2 py-0.5 rounded-full text-white text-[11px] font-medium"
                  :class="riskBg(p.READMIT_RISK)"
                >{{ (p.READMIT_RISK * 100).toFixed(0) }}%</span>
              </td>
              <td class="px-3 py-2 text-gray-600">{{ p.LACE_SCORE }}</td>
              <td class="px-3 py-2">
                <span
                  class="inline-block w-2 h-2 rounded-full"
                  :class="p.READMITTED ? 'bg-red-500' : 'bg-green-500'"
                  :title="p.READMITTED ? 'Readmitted' : 'Not readmitted'"
                />
              </td>
              <td class="px-3 py-2">
                <span
                  class="px-1.5 py-0.5 rounded text-[10px] font-medium"
                  :class="confidenceClass(p.CONFIDENCE)"
                >{{ p.CONFIDENCE }}</span>
              </td>
              <td class="px-3 py-2 text-gray-500 max-w-[300px] truncate">
                {{ topRiskFactors(p) }}
              </td>
              <td class="px-3 py-2 text-center">
                <span
                  v-if="predictionCorrect(p)"
                  class="text-green-600"
                  title="Correct prediction"
                >✓</span>
                <span
                  v-else
                  class="text-red-500"
                  title="Incorrect prediction"
                >✗</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && filteredPredictions.length > pageSize" class="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50">
        <span class="text-xs text-gray-500">
          {{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, filteredPredictions.length) }}
          of {{ filteredPredictions.length }}
        </span>
        <div class="flex gap-1">
          <button
            class="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
            :disabled="currentPage <= 1"
            @click="currentPage--"
          >Prev</button>
          <button
            class="px-2 py-1 text-xs rounded border border-gray-200 hover:bg-gray-100 disabled:opacity-40"
            :disabled="currentPage >= totalPages"
            @click="currentPage++"
          >Next</button>
        </div>
      </div>
    </div>

    <!-- Right panel: patient detail -->
    <div v-if="selectedPrediction" class="w-1/2 border-l border-gray-200 overflow-hidden flex flex-col bg-white">
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
        <div class="flex items-center gap-3">
          <span class="text-xs font-mono font-medium text-gray-700">{{ selectedPrediction.MBI }}</span>
          <span
            class="inline-block px-2 py-0.5 rounded-full text-white text-xs font-medium"
            :class="riskBg(selectedPrediction.READMIT_RISK)"
          >{{ (selectedPrediction.READMIT_RISK * 100).toFixed(0) }}% risk</span>
          <span class="text-xs text-gray-500">LACE {{ selectedPrediction.LACE_SCORE }}</span>
          <span class="flex items-center gap-1 text-xs" :class="selectedPrediction.READMITTED ? 'text-red-600' : 'text-green-600'">
            <span class="w-2 h-2 rounded-full" :class="selectedPrediction.READMITTED ? 'bg-red-500' : 'bg-green-500'" />
            {{ selectedPrediction.READMITTED ? 'Readmitted' : 'Not readmitted' }}
          </span>
        </div>
        <button class="text-gray-400 hover:text-gray-600 p-1" @click="selectedPrediction = null">
          <font-awesome-icon :icon="['fas', 'xmark']" />
        </button>
      </div>

      <!-- Detail content -->
      <div class="flex-1 overflow-auto">
        <!-- Scores comparison -->
        <div class="px-4 py-3 border-b border-gray-100">
          <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Score Comparison</div>
          <div class="flex gap-4">
            <div class="flex-1 rounded-lg p-3" :class="riskBgLight(selectedPrediction.READMIT_RISK)">
              <div class="text-[10px] uppercase tracking-wider text-gray-500 font-medium">AI Risk Score</div>
              <div class="text-2xl font-bold mt-0.5" :class="riskText(selectedPrediction.READMIT_RISK)">
                {{ (selectedPrediction.READMIT_RISK * 100).toFixed(0) }}%
              </div>
              <div class="text-[10px] mt-0.5" :class="riskText(selectedPrediction.READMIT_RISK)">
                {{ selectedPrediction.CONFIDENCE }} confidence
              </div>
            </div>
            <div class="flex-1 rounded-lg p-3 bg-gray-50">
              <div class="text-[10px] uppercase tracking-wider text-gray-500 font-medium">LACE Score</div>
              <div class="text-2xl font-bold mt-0.5 text-gray-700">
                {{ selectedPrediction.LACE_SCORE }}<span class="text-sm font-normal text-gray-400"> / 16</span>
              </div>
              <div class="text-[10px] text-gray-400 mt-0.5">
                {{ selectedPrediction.LACE_SCORE <= 8 ? 'Low risk' : selectedPrediction.LACE_SCORE <= 12 ? 'Moderate risk' : 'High risk' }}
              </div>
            </div>
            <div class="flex-1 rounded-lg p-3" :class="predictionCorrect(selectedPrediction) ? 'bg-green-50' : 'bg-red-50'">
              <div class="text-[10px] uppercase tracking-wider text-gray-500 font-medium">Prediction</div>
              <div class="text-2xl font-bold mt-0.5" :class="predictionCorrect(selectedPrediction) ? 'text-green-700' : 'text-red-600'">
                {{ predictionCorrect(selectedPrediction) ? '✓' : '✗' }}
              </div>
              <div class="text-[10px] mt-0.5" :class="predictionCorrect(selectedPrediction) ? 'text-green-600' : 'text-red-500'">
                {{ predictionCorrect(selectedPrediction) ? 'Correct' : 'Incorrect' }}
              </div>
            </div>
          </div>
        </div>

        <!-- Model reasoning -->
        <div class="px-4 py-3 border-b border-gray-100">
          <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Model Reasoning</div>
          <p class="text-xs text-gray-700 leading-relaxed">{{ selectedPrediction.REASONING }}</p>
        </div>

        <!-- Risk factors -->
        <div class="px-4 py-3 border-b border-gray-100">
          <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Risk Factors</div>
          <div class="flex flex-wrap gap-1.5">
            <span
              v-for="(rf, i) in parsedRiskFactors(selectedPrediction)"
              :key="i"
              class="px-2.5 py-1 bg-amber-50 text-amber-800 text-xs rounded-full border border-amber-200"
            >{{ rf }}</span>
          </div>
        </div>

        <!-- What LACE misses -->
        <div v-if="selectedPrediction.WHAT_LACE_MISSES" class="px-4 py-3">
          <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">What LACE Misses</div>
          <p class="text-xs text-gray-500 leading-relaxed">{{ selectedPrediction.WHAT_LACE_MISSES }}</p>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CareManagement_AIRiskScore',
  data() {
    return {
      loading: true,
      predictions: [],
      selectedPrediction: null,
      sortField: 'READMIT_RISK',
      sortDir: 'desc',
      currentPage: 1,
      pageSize: 100,
      activeOutcome: 'all',
      outcomeFilters: [
        { label: 'All', value: 'all' },
        { label: 'Readmitted', value: 'readmitted' },
        { label: 'Not Readmitted', value: 'not_readmitted' },
        { label: 'LACE Missed', value: 'lace_missed' },
      ],
      columns: [
        { field: 'MBI', label: 'MBI' },
        { field: 'DISCHARGE_DT', label: 'Discharge' },
        { field: 'READMIT_RISK', label: 'Risk Score' },
        { field: 'LACE_SCORE', label: 'LACE' },
        { field: 'READMITTED', label: 'Outcome' },
        { field: 'CONFIDENCE', label: 'Conf.' },
        { field: 'RISK_FACTORS', label: 'Risk Factors', sortable: false },
        { field: '_correct', label: '', sortable: false },
      ],
    }
  },
  computed: {
    filteredPredictions() {
      let list = this.predictions
      if (this.activeOutcome === 'readmitted') list = list.filter(p => p.READMITTED)
      else if (this.activeOutcome === 'not_readmitted') list = list.filter(p => !p.READMITTED)
      else if (this.activeOutcome === 'lace_missed') list = list.filter(p => p.READMITTED && p.READMIT_RISK >= 0.6 && p.LACE_SCORE <= 8)

      const field = this.sortField
      const dir = this.sortDir === 'asc' ? 1 : -1
      return [...list].sort((a, b) => {
        const av = a[field] ?? ''
        const bv = b[field] ?? ''
        if (av < bv) return -dir
        if (av > bv) return dir
        return 0
      })
    },
    totalPages() {
      return Math.ceil(this.filteredPredictions.length / this.pageSize)
    },
    paginatedPredictions() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredPredictions.slice(start, start + this.pageSize)
    },
  },
  watch: {
    activeOutcome() { this.currentPage = 1 },
  },
  mounted() {
    this.fetchPredictions()
  },
  methods: {
    async fetchPredictions() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/care-management/readmission-risk/')
        this.predictions = data.predictions || []
      } finally {
        this.loading = false
      }
    },
    selectPatient(p) {
      if (this.isSelected(p)) {
        this.selectedPrediction = null
      } else {
        this.selectedPrediction = p
      }
    },
    isSelected(p) {
      return this.selectedPrediction && p.MBI === this.selectedPrediction.MBI && p.INDEX_CLM_ID === this.selectedPrediction.INDEX_CLM_ID
    },
    toggleSort(field) {
      if (this.sortField === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDir = field === 'READMIT_RISK' ? 'desc' : 'asc'
      }
    },
    riskBg(score) {
      if (score >= 0.7) return 'bg-red-500'
      if (score >= 0.4) return 'bg-amber-500'
      return 'bg-green-500'
    },
    riskBgLight(score) {
      if (score >= 0.7) return 'bg-red-50'
      if (score >= 0.4) return 'bg-amber-50'
      return 'bg-green-50'
    },
    riskText(score) {
      if (score >= 0.7) return 'text-red-700'
      if (score >= 0.4) return 'text-amber-700'
      return 'text-green-700'
    },
    confidenceClass(conf) {
      if (conf === 'high') return 'bg-green-50 text-green-700'
      if (conf === 'medium') return 'bg-amber-50 text-amber-700'
      return 'bg-gray-100 text-gray-600'
    },
    predictionCorrect(p) {
      const predicted = p.READMIT_RISK >= 0.5
      return predicted === !!p.READMITTED
    },
    topRiskFactors(p) {
      return this.parsedRiskFactors(p).slice(0, 3).join(', ')
    },
    parsedRiskFactors(p) {
      if (!p.RISK_FACTORS) return []
      if (Array.isArray(p.RISK_FACTORS)) return p.RISK_FACTORS
      try { return JSON.parse(p.RISK_FACTORS) } catch { return [] }
    },
  },
}
</script>
